import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TID } from '../lib/testIds';

const links = [
  { id: 'reels', label: 'Work', testid: TID.navLinkWork },
  { route: '/portfolio', label: 'Portfolio', testid: TID.navLinkPortfolio },
  { id: 'packages', label: 'Services', testid: TID.navLinkServices },
  { id: 'about', label: 'About', testid: TID.navLinkAbout },
  { id: 'contact', label: 'Contact', testid: TID.navLinkContact },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      return;
    }
    const el = document.getElementById(id);
    if (!el) return;
    // Use Lenis if available (native scroll gets overridden by Lenis' RAF loop).
    if (window.lenis) {
      window.lenis.scrollTo(el, { offset: -70, duration: 1.1 });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handle = (l) => {
    setOpen(false);
    if (l.route) {
      navigate(l.route);
      window.scrollTo(0, 0);
      return;
    }
    goSection(l.id);
  };

  const goLogo = () => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      window.scrollTo(0, 0);
      return;
    }
    if (window.lenis) {
      window.lenis.scrollTo(0, { duration: 1.1 });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <motion.nav
      data-testid={TID.navRoot}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#050505]/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-10 lg:px-14 py-5">
        <button
          data-testid={TID.navLogo}
          onClick={goLogo}
          className="font-display text-xl md:text-2xl font-black tracking-tight text-white"
        >
          UT<span className="text-white/40">/</span>MEDIA
        </button>

        <ul className="hidden md:flex items-center gap-8 lg:gap-12">
          {links.map((l) => (
            <li key={l.label}>
              <button
                data-testid={l.testid}
                onClick={() => handle(l)}
                aria-current={l.route && location.pathname === l.route ? 'page' : undefined}
                className={`group relative text-xs uppercase tracking-[0.22em] transition-colors ${
                  l.route && location.pathname === l.route
                    ? 'text-[#D8FF3A] neon-text-glow'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-white transition-all duration-500 group-hover:w-full" />
              </button>
            </li>
          ))}
        </ul>

        <button
          data-testid={TID.navCta}
          onClick={() => goSection('contact')}
          className="hidden md:inline-flex items-center gap-2 bg-[#D8FF3A] text-black px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold hover:neon-glow hover:bg-white transition-all"
        >
          Start a project
          <span aria-hidden>→</span>
        </button>

        <button
          aria-label="Open menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl"
          >
            <ul className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <li key={l.label}>
                  <button
                    data-testid={`${l.testid}-mobile`}
                    onClick={() => handle(l)}
                    className="font-display text-3xl font-bold text-white hover:text-white/70 transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
              <li>
                <button
                  data-testid={`${TID.navCta}-mobile`}
                  onClick={() => {
                    setOpen(false);
                    goSection('contact');
                  }}
                  className="mt-2 inline-flex items-center gap-2 bg-white text-black px-5 py-3 text-xs uppercase tracking-[0.2em] font-semibold"
                >
                  Start a project →
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
